import { frameNr } from '.';
import logger from './controllers/logger';
import { lerpNumber, lerpSize, lerpV2, removeDenseArrayIndex } from './helpers';
import {
  AnchoredPosition,
  AnimationType,
  DeltaTime,
  TransformRadius,
  TransformSize,
  type PlaygroundAnimationCollection,
  type TransformFullSize,
} from './types';

export function updateAnimations(animations: PlaygroundAnimationCollection[], dt: DeltaTime) {
  for (let i = animations.length - 1; i >= 0; i--) {
    if (animations[i].doKill) {
      removeDenseArrayIndex(animations, i);
      continue;
    }
    const anim = animations[i];

    anim.t += dt;
    if (anim.t > anim.delay) {
      const time = anim.t - anim.delay;
      if (time >= anim.duration) {
        anim.t = anim.delay + anim.duration;
        anim.p = 1;
      } else {
        anim.p = time / anim.duration;
      }
    } else {
      anim.p = 0;
    }

    anim.pEased = anim.easeFunction(anim.p);

    switch (anim.type) {
      case AnimationType.SIZE:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.TRANSFORM_SIZE:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.SIZE_FULL:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.ROTATION:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.NUMBER:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.SCALE:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.OPACITY:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.ANCHORED:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.SHAPE_STYLE:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.IMAGE_SIZE:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      case AnimationType.POSITION:
        anim.currentValue = anim.lerpFunction(anim.start, anim.end, anim.pEased);
        break;
      default:
        logger.debug('unknown animation type', anim.type);
    }
  }
}

export function lerpTransformSize(
  from: TransformSize,
  to: TransformSize,
  t: number,
): TransformSize {
  return {
    sizePx: to.sizePx && lerpSize(from.sizePx, to.sizePx, t),
    sizeEm: to.sizeEm && lerpSize(from.sizeEm, to.sizeEm, t),
  };
}

export function lerpTransformFullSize(
  start: TransformFullSize,
  end: TransformFullSize,
  t: number,
): TransformFullSize {
  return {
    width: end.width && lerpNumber(start.width, end.width, t),
    height: end.height && lerpNumber(start.height, end.height, t),
    relativeWidth: end.relativeWidth && lerpNumber(start.relativeWidth, end.relativeWidth, t),
    relativeHeight: end.relativeHeight && lerpNumber(start.relativeHeight, end.relativeHeight, t),
    padWidth: end.padWidth && lerpNumber(start.padWidth, end.padWidth, t),
    padHeight: end.padHeight && lerpNumber(start.padHeight, end.padHeight, t),
    maxWidth: end.maxWidth && lerpNumber(start.maxWidth, end.maxWidth, t),
    maxHeight: end.maxHeight && lerpNumber(start.maxHeight, end.maxHeight, t),
    maxAspect: end.maxAspect && lerpNumber(start.maxAspect, end.maxAspect, t),
  };
}

export function lerpTransformRadius(
  from: TransformRadius,
  to: TransformRadius,
  t: number,
): TransformRadius {
  return {
    radiusPx: to.radiusPx && lerpNumber(from.radiusPx, to.radiusPx, t),
    radiusEm: to.radiusEm && lerpNumber(from.radiusEm, to.radiusEm, t),
  };
}

//Todo remove null checks.
//anchored animations should handle from and to null before the lerp
export function lerpAnchoredPosition(
  from: AnchoredPosition,
  to: AnchoredPosition,
  t: number,
): AnchoredPosition {
  return {
    referenceId: from.referenceId,
    relativePos: from.relativePos ? lerpV2(from.relativePos, to.relativePos, t) : null,
    offset: lerpV2(from.offset, to.offset, t),
    relativePivotPos: lerpV2(from.relativePivotPos, to.relativePivotPos, t),
  };
}
